import type { NavItem } from "@/types/nav-item.types";
import { DirectPaths } from "@/config/routes";

/** Product surfaces that use logo-reload / special Home-crumb behavior. */
export type ProductNavKind = "stocks" | "global" | "gold" | "silver" | "fd";

/**
 * Pathname-based product detection (preferred over nav `active` for Silver,
 * which shares the bullion layout’s Gold active flag).
 */
export function getProductNavKind(pathname: string): ProductNavKind | null {
    const path = pathname || "";

    if (
        path === DirectPaths.STOCKS ||
        path.startsWith(`${DirectPaths.STOCKS}/`) ||
        path === "/broking" ||
        path.startsWith("/broking/")
    ) {
        return "stocks";
    }

    if (
        path === DirectPaths.GLOBAL ||
        path.startsWith(`${DirectPaths.GLOBAL}/`) ||
        path === DirectPaths.US_STOCKS ||
        path.startsWith(`${DirectPaths.US_STOCKS}/`) ||
        path === DirectPaths.US_ETFS ||
        path.startsWith(`${DirectPaths.US_ETFS}/`)
    ) {
        return "global";
    }

    // Silver before gold — paths are disjoint but keep order explicit.
    if (
        path.includes(`${DirectPaths.BULLION_POSTFIX_PATH}/silver`) ||
        path.includes("/bullion/silver")
    ) {
        return "silver";
    }

    if (
        path.includes(`${DirectPaths.BULLION_POSTFIX_PATH}/gold`) ||
        path.includes("/bullion/gold")
    ) {
        return "gold";
    }

    if (
        path === DirectPaths.FD_POSTFIX_PATH ||
        path.startsWith(`${DirectPaths.FD_POSTFIX_PATH}/`) ||
        path === DirectPaths.FD ||
        path.startsWith(`${DirectPaths.FD}/`)
    ) {
        return "fd";
    }

    return null;
}

/** Logo click should hard-reload the current URL on these product pages. */
export function isProductLogoReloadPath(pathname: string): boolean {
    const kind = getProductNavKind(pathname);
    return (
        kind === "stocks" ||
        kind === "global" ||
        kind === "gold" ||
        kind === "silver" ||
        kind === "fd"
    );
}

/**
 * Post-login Home crumb destination for Stocks / Global (“Refresh → Post-login”).
 * Returns null for products that should use Platform instead.
 */
export function getPostLoginProductListingUrl(pathname: string): string | null {
    const kind = getProductNavKind(pathname);
    if (kind === "stocks") {
        return DirectPaths.STOCKS;
    }
    if (kind === "global") {
        const isEtf =
            pathname.includes(DirectPaths.US_ETFS) || pathname.includes("us-etfs");
        const base = (process.env.ICM_GLOBAL_POST_URL || "").replace(/\/+$/, "");
        if (!base) {
            return `${DirectPaths.GLOBAL}${isEtf ? DirectPaths.US_ETFS : DirectPaths.US_STOCKS}`;
        }
        return `${base}${DirectPaths.GLOBAL}${isEtf ? DirectPaths.US_ETFS : DirectPaths.US_STOCKS}`;
    }
    return null;
}

/**
 * Resolves the logo URL for the currently active nav item (or its active sub-item).
 * When `preferMobile` is true, uses `mobileLogoUrl` if the active item provides one.
 * Dedicated mobile logos skip `transform` (they are already theme-ready assets).
 */
export function getActiveNavLogoUrl(
    navItems: NavItem[],
    defaultLogoUrl: string,
    transform?: (url: string) => string,
    preferMobile = false,
): string {
    const apply = (url?: string, skipTransform = false): string => {
        if (!url) return defaultLogoUrl;
        return transform && !skipTransform ? transform(url) : url;
    };

    for (const item of navItems) {
        const activeSubItem = item.subItems?.find((sub) => sub.isActive);

        if (activeSubItem) {
            if (preferMobile) {
                const mobileUrl =
                    activeSubItem.mobileLogoUrl ?? item.mobileLogoUrl;
                if (mobileUrl) return apply(mobileUrl, true);
                return apply(activeSubItem.logoUrl ?? item.logoUrl);
            }
            return apply(activeSubItem.logoUrl ?? item.logoUrl);
        }

        if (item.isActive) {
            if (preferMobile && item.mobileLogoUrl) {
                return apply(item.mobileLogoUrl, true);
            }
            return apply(item.logoUrl);
        }
    }

    return defaultLogoUrl;
}

/** Prefer an in-app path so logo clicks stay on the current origin. */
function toAppPath(href: string): string {
    if (href.startsWith("/")) return href;
    try {
        const { pathname, search, hash } = new URL(href);
        return `${pathname}${search}${hash}` || "/";
    } catch {
        return href;
    }
}

/**
 * True when the header shows a product-specific logo (stocks, global, gold, FD, …).
 * Shares the same `isActive` + `logoUrl` signals as `getActiveNavLogoHref`.
 */
export function hasActiveProductNavLogo(navItems: NavItem[]): boolean {
    for (const item of navItems) {
        const activeSubItem = item.subItems?.find((sub) => sub.isActive);

        if (activeSubItem && (activeSubItem.logoUrl || item.logoUrl)) {
            if (activeSubItem.href || item.href) return true;
        }

        if (item.isActive && item.logoUrl && item.href) {
            return true;
        }
    }

    return false;
}

/**
 * Resolves the logo click destination for the active product nav item.
 * Uses the same `isActive` + `logoUrl` signals as `getActiveNavLogoUrl`, so
 * whenever a page-specific logo is shown, click goes to that product listing.
 * Falls back to `defaultHref` (home / platform) when no product logo is active.
 */
export function getActiveNavLogoHref(
    navItems: NavItem[],
    defaultHref: string,
): string {
    for (const item of navItems) {
        const activeSubItem = item.subItems?.find((sub) => sub.isActive);

        if (activeSubItem && (activeSubItem.logoUrl || item.logoUrl)) {
            const href = activeSubItem.href || item.href;
            if (href) return toAppPath(href);
        }

        if (item.isActive && item.logoUrl && item.href) {
            return toAppPath(item.href);
        }
    }

    return defaultHref;
}
